import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import './index.css';
import App from './App';

// Top-priority routes for idle-time prefetch
const PREFETCH_ROUTES = [
  '/inventory',
  '/financing',
  '/broker-services',
  '/contact',
  '/sell-your-car',
  '/trade-in',
  '/consignment',
  '/military-discount',
  '/reviews',
  '/how-it-works',
];

// ── Mount React immediately — nothing should block this ──
createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <App />
  </StrictMode>
);

// ── Defer ALL non-critical resources until after LCP ──
// This eliminates the 12.6s critical path waterfall by loading
// fonts and icons asynchronously after first paint.
const afterLCP = (fn: () => void, delay = 0) => {
  if ('requestIdleCallback' in window) {
    requestIdleCallback(fn, { timeout: delay + 2000 });
  } else {
    setTimeout(fn, delay);
  }
};

// Load Google Fonts after LCP - no blocking
afterLCP(() => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap';
  document.head.appendChild(link);
}, 100);

// Load Font Awesome after LCP - no blocking
afterLCP(() => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.5.1/css/all.min.css';
  link.onload = () => {
    document.documentElement.classList.add('fonts-loaded');
  };
  document.head.appendChild(link);
}, 200);

// Load Remix Icon after LCP - no blocking
afterLCP(() => {
  const link = document.createElement('link');
  link.rel = 'stylesheet';
  link.href = 'https://cdnjs.cloudflare.com/ajax/libs/remixicon/4.5.0/remixicon.min.css';
  document.head.appendChild(link);
}, 300);

// Analytics — load after page is fully interactive
afterLCP(() => {
  const script = document.createElement('script');
  script.src = 'https://public.readdy.ai/analysis/event-reporting.min.js';
  script.defer = true;
  document.body.appendChild(script);
}, 1000);

// LCP observer — install after paint so it doesn't block render
afterLCP(() => {
  import('./utils/routePrefetch').then(({ installLCPObserver }) => {
    installLCPObserver();
  }).catch(() => {});
}, 1500);

// Service Worker Registration
if ('serviceWorker' in navigator) {
  // Defer SW registration until after load event — never block LCP
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/sw.js', { scope: '/' })
      .then((registration) => {
        // Check for updates every hour
        setInterval(() => { registration.update().catch(() => {}); }, 60 * 60 * 1000);

        // Handle updates — auto-apply silently
        registration.addEventListener('updatefound', () => {
          const newWorker = registration.installing;
          if (!newWorker) return;
          newWorker.addEventListener('statechange', () => {
            if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
              newWorker.postMessage('SKIP_WAITING');
            }
          });
        });

        // Auto-reload when new SW takes control
        let refreshing = false;
        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if (!refreshing) { refreshing = true; window.location.reload(); }
        });

        // Idle-time route prefetch — warm secondary routes after app is interactive
        const dispatchPrefetch = () => {
          if (navigator.serviceWorker.controller) {
            navigator.serviceWorker.controller.postMessage({
              type: 'PREFETCH_ROUTES',
              routes: PREFETCH_ROUTES,
            });
          }
        };

        if ('requestIdleCallback' in window) {
          requestIdleCallback(dispatchPrefetch, { timeout: 5000 });
        } else {
          setTimeout(dispatchPrefetch, 5000);
        }

        navigator.serviceWorker.addEventListener('controllerchange', () => {
          if ('requestIdleCallback' in window) {
            requestIdleCallback(dispatchPrefetch, { timeout: 5000 });
          } else {
            setTimeout(dispatchPrefetch, 5000);
          }
        });
      })
      .catch(() => {
        // Non-fatal — site works fine without SW
      });
  });
}